import { z } from 'zod';
import { IsoTimestamp, Version } from '../primitives.js';
import { FeatureScope, RiskLevel } from '../enums.js';

/**
 * §11.1 / §27.22 FeatureDefinition — every independently usable capability has one.
 * `valueSchema` is a JSON-schema-ish descriptor; concrete zod schemas live in the
 * feature-flags registry keyed by featureKey.
 */
export const FeatureDefinition = z.object({
  featureKey: z.string().min(1),
  definitionVersion: Version.default(1),
  displayName: z.string().min(1),
  description: z.string().default(''),
  valueType: z.enum(['boolean', 'object', 'enum']),
  defaultValue: z.unknown(),
  allowedScopes: z.array(FeatureScope).min(1),
  requiredRole: z.enum(['owner', 'admin']).default('admin'),
  dependencies: z.array(z.string()).default([]),
  conflicts: z.array(z.string()).default([]),
  requiredIntegration: z.enum(['stripe', 'ntfy']).nullable().default(null),
  riskLevel: RiskLevel.default('low'),
  affectsPublicSnapshot: z.boolean().default(false),
  hasKillSwitch: z.boolean().default(false),
  status: z.enum(['active', 'deprecated']).default('active'),
});
export type FeatureDefinition = z.infer<typeof FeatureDefinition>;

/** §27.23 FeatureOverride — a value applied at one scope. */
export const FeatureOverride = z.object({
  scopeType: FeatureScope,
  scopeId: z.string(),
  featureKey: z.string(),
  value: z.unknown(),
  definitionVersion: Version.default(1),
  setBy: z.string(),
  setAt: IsoTimestamp,
  reason: z.string().max(280).optional(),
  version: Version,
});
export type FeatureOverride = z.infer<typeof FeatureOverride>;

/** §11.2 Effective-feature response returned by the admin API. */
export const EffectiveFeature = z.object({
  key: z.string(),
  enabled: z.boolean(),
  value: z.unknown(),
  source: FeatureScope,
  dependencies: z.record(z.string(), z.boolean()).default({}),
  blockedBy: z.array(z.string()).default([]),
  evaluatedAt: IsoTimestamp,
  definitionVersion: Version,
});
export type EffectiveFeature = z.infer<typeof EffectiveFeature>;
